import { BOSSES, INCURSION, INCURSION_SPAWN_SPACING, NIGHT_ALLIES, NIGHTMARES, PLUNDER, SAGA, SIDE, SIDES, UNSLEEPING, WAVES_PER_MAREA, type NightAlly, type NightmareType, type EnemyKind, type HeroClass, type SideAct, type SideBattle, type SideChapter, type SideId, type SideSaga, type SideScene } from '@somnhold/content';
import type { BattleParams } from '../battle/create.ts';
import { DIRS, hexDist, hexLen, hexRing, hexToWorld, ROW, type Hex } from '../hex.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';
import { dist2, idiv, imax, imin, isqrt, mulBp } from '../math/fixed.ts';
import { snapshot } from '../math/hash.ts';
import type { BattleState } from '../types.ts';
import { addRes, event, findPlayer, heroName } from './core.ts';
import { giveUnique, grantSeal, hasUnique, lookOf, promiseUnique } from './items.ts';
import { groupLevel } from './eras.ts';
import { grantXp } from './progress.ts';
import { afterUnsleeping } from './unsleeping.ts';
import { plunderCaches, plunderSpawns } from './incursion.ts';
import { descent, field, party, sagaOf, type SagaResult, type SagaTicket } from './saga.ts';
import { waveBattleParams } from './resolve.ts';
import { bossHp } from '../wave.ts';
import type { CampaignState, CommandResult, Ms, SideHunt, SideState } from './types.ts';

/**
 * The sagas beside the Turtle's (docs/SAGAS.md): one chapter at a time for the whole group, act after act, anyone
 * may play the next one. Scenes move on with a command; fights are issued like the saga's acts (their ticket carries
 * `side`), re-simulated and applied here. Losing a fight costs nothing: it is tried again whenever. The clues a
 * fight's heroes pick up stay on the group's board, and are not laid out again.
 */

const fresh = (): SideState => ({ chapter: 0, act: 0, played: [], tries: 0, clues: [], done: [] });

export const sideDef = (id: string): SideSaga | undefined => SIDES.find((x) => x.id === id);

export const sideOf = (s: CampaignState, id: SideId): SideState => s.sides?.[id] ?? fresh();

const sideMut = (s: CampaignState, id: SideId): SideState => ((s.sides ??= {})[id] ??= fresh());

export const sideChapter = (s: CampaignState, id: SideId): SideChapter | null => sideDef(id)?.chapters[sideOf(s, id).chapter] ?? null;

export const sideAct = (s: CampaignState, id: SideId): SideAct | null => sideChapter(s, id)?.acts[sideOf(s, id).act] ?? null;

/** La Quemadura's marks, one per chapter done (docs/QUEMADURA.md, «Memoria»). */
const BURN_MARKS = ['quemadura.mapa', 'quemadura.planos', 'quemadura.diario', 'quemadura.faro'];

export type SideBlock = 'over' | 'soon' | 'level' | 'wait' | 'campaignLost';

/** Why the next act can't be played yet (null: it can). A chapter begun never closes again. */
export const sideBlocked = (s: CampaignState, id: SideId): SideBlock | null => {
  const def = sideDef(id);
  const st = sideOf(s, id);
  if (!def || st.chapter >= def.chapters.length) return 'over';
  if (st.chapter >= def.built) return 'soon';
  if (s.status === 'lost') return 'campaignLost';
  const ch = def.chapters[st.chapter] as SideChapter;
  const begun = st.act > 0 || st.played.length > 0;
  if (!begun && groupLevel(s, s.clock) < ch.minGroup) return 'level';
  if (!begun && sideOpensAt(s, id) > s.clock) return 'wait';
  return null;
};

/** When the chapter being played opens, if it waits on the one before it (0: it doesn't wait). */
export const sideOpensAt = (s: CampaignState, id: SideId): Ms => {
  const st = sideOf(s, id);
  const wait = sideDef(id)?.chapters[st.chapter]?.wait;
  const before = st.doneAt?.[st.chapter - 1];
  return wait && before !== undefined ? before + wait : 0;
};

/** A chapter done: its reward to the storage, XP to whoever played an act of it, and on to the next. */
const advance = (s: CampaignState, id: SideId, pids: string[], now: Ms): void => {
  const def = sideDef(id) as SideSaga;
  const st = sideMut(s, id);
  for (const pid of pids) if (!st.played.includes(pid)) st.played.push(pid);
  st.act++;
  const ch = def.chapters[st.chapter] as SideChapter;
  if (st.act < ch.acts.length) return;
  addRes(s.storage, SIDE.reward[def.difficulty]);
  for (const pid of st.played) if (findPlayer(s, pid)) grantXp(s, pid, SIDE.xp, now);
  // La Orilla 5: the amulet of the hero's class (docs/UNICAS.md §1), now or kept until its level; never a second one.
  if (ch.unique)
    for (const pid of st.played) {
      const p = findPlayer(s, pid);
      if (p) promiseUnique(s, p, ch.unique, ch.uniqueLevel ?? 1, now);
    }
  st.done[st.chapter] = [...st.played];
  (st.doneAt ??= [])[st.chapter] = now;
  // El Cuerno 1: the group saw Cal herding up in the tail, which Caras prestadas' board remembers.
  if (id === 'horn' && st.chapter === 0) give(sideMut(s, 'faces'), ['calHerding'], st.played[0] ?? '', now);
  event(s, now, 'side', { data: { saga: id, chapter: st.chapter, kind: 'done', party: st.played.join(',') } });
  st.chapter++;
  st.act = 0;
  st.played = [];
};

/** Clues a scene or a won fight leaves on the board by itself (once each). */
const give = (st: SideState, ids: string[] | undefined, pid: string, now: Ms): void => {
  for (const c of ids ?? []) if (!st.clues.some((x) => x.id === c)) st.clues.push({ id: c, pid, at: now });
};

/**
 * The marks a saga's scenes and fights read: what the group decided, and what happened elsewhere. El Cuerno: Ragna met
 * before and gone in smoke (`horn.met`), or already felled in another fight (`horn.felled`; her own fall in the
 * graveyard is `horn.slain`, not this).
 */
/** Every chapter of a side saga done. */
const sideDone = (s: CampaignState, id: SideId): boolean => {
  const def = SIDES.find((x) => x.id === id);
  return !!def && def.chapters.every((_, i) => !!s.sides?.[id]?.done[i]);
};

/** The Turtle's saga done (its dragon beaten) with this mark set on the way: Gaspar or Maelis come to the end. */
const turtleMark = (s: CampaignState, flag: string): boolean => sagaOf(s).chapter >= 7 && sagaOf(s).flags.includes(flag);

export const sideFlags = (s: CampaignState, id: SideId): string[] => {
  const flags = sideOf(s, id).flags ?? [];
  // La Quemadura: each chapter done leaves its mark (the advantages against the dragon read them).
  if (id === 'burn') return [...flags, ...BURN_MARKS.filter((_, i) => !!sideOf(s, id).done[i]), ...(sideOf(s, id).done[3] ? ['orla.ayuda'] : [])];
  // Las vías: Bruni met before and gone in smoke (`rails.met`), or already fallen in another fight (`rails.felled`; his fall at the forge is `rails.slain`).
  if (id === 'rails') {
    const b = s.unsleeping?.bruni;
    if (b?.fallen && !flags.includes('rails.slain')) return [...flags, 'rails.felled'];
    return b && !b.fallen && !b.home ? [...flags, 'rails.met'] : flags;
  }
  // El pozo: the group already met Odna, and saw the caravan come in whole (the Turtle's first chapter done).
  // La Orilla: La Quemadura's diary read (its third chapter done).
  // …and the Turtle's dragon already beaten (its seventh chapter done): Brann knows this one is a different one.
  if (id === 'shore') return [...flags, ...(s.sides?.burn?.done[2] ? ['shore.diary'] : []), ...((s.saga?.chapter ?? 0) >= 7 ? ['shore.burned'] : [])];
  // El Párpado: La Quemadura done (Orla knows the andariegos never mapped the head).
  // …and El Cuerno done (Ulla sends her stone boars to drag the bell).
  // …and the rest of what the group did, for who comes to the night of the bell and who speaks after it.
  if (id === 'lid')
    return [
      ...flags,
      ...(s.sides?.burn?.done[3] ? ['lid.orla'] : []),
      ...(s.sides?.horn?.done[3] ? ['lid.boars'] : []),
      ...(sideDone(s, 'faces') ? ['lid.faces'] : []),
      ...(sideDone(s, 'rails') ? ['lid.rails'] : []),
      ...(sideDone(s, 'well') ? ['lid.well'] : []),
      ...(sideDone(s, 'shore') ? ['lid.shore'] : []),
      ...(turtleMark(s, 'gaspar.heard') ? ['lid.gaspar'] : []),
      ...(turtleMark(s, 'maelis.knows') ? ['lid.maelis'] : []),
    ];
  if (id === 'well') return (s.saga?.chapter ?? 0) >= 1 ? [...flags, 'well.caravan'] : flags;
  if (id !== 'horn') return flags;
  const r = s.unsleeping?.ragna;
  const out = [...flags];
  if (r?.fallen && !flags.includes('horn.slain')) out.push('horn.felled');
  else if (r && !r.fallen && !r.home) out.push('horn.met');
  return out;
};

/** What a scene puts to the vote (nothing, when what happened elsewhere settled it). */
export const sideChoices = (s: CampaignState, id: SideId, sc: SideScene): NonNullable<SideScene['choices']> =>
  sc.unless && sideFlags(s, id).includes(sc.unless) ? [] : (sc.choices ?? []);

/** The lines of a scene this group gets to hear (some depend on what it decided). */
export const sideLines = (s: CampaignState, id: SideId, sc: SideScene): SideScene['lines'] => {
  const flags = sideFlags(s, id);
  return sc.lines.filter((l) => (!l.if?.flag || flags.includes(l.if.flag)) && (!l.if?.notFlag || !flags.includes(l.if.notFlag)));
};

/** A scene played through: the way on (the act must be the one the saga is at, and a scene with no decision). */
export const applySideGo = (s: CampaignState, pid: string, id: string, chapter: number, act: number, now: Ms): CommandResult => {
  const def = sideDef(id);
  if (!def) return { ok: false, reason: 'unknownSaga' };
  const sid = def.id;
  const st = sideOf(s, sid);
  if (st.chapter !== chapter || st.act !== act) return { ok: false, reason: 'moved' };
  if (sideBlocked(s, sid)) return { ok: false, reason: 'sideClosed' };
  const sc = sideAct(s, sid);
  if (sc?.kind !== 'scene') return { ok: false, reason: 'notAScene' };
  if (sideChoices(s, sid, sc).length) return { ok: false, reason: 'decision' };
  give(sideMut(s, sid), sc.given, pid, now);
  advance(s, sid, [pid], now);
  return { ok: true };
};

/** Who gets a say: every hero still in the campaign. */
const voters = (s: CampaignState): number => imax(1, s.players.filter((p) => !p.left).length);

/**
 * Closes a side saga's vote if it is decided (everyone voted, or one side has more than half the group), or if
 * `force`: the most voted side wins, a tie goes to whoever opened it. The winner's marks are set and the saga moves on.
 */
const decideSide = (s: CampaignState, id: SideId, now: Ms, force: boolean): boolean => {
  const st = sideMut(s, id);
  const v = st.vote;
  if (!v) return false;
  const sc = sideDef(id)?.chapters[v.chapter]?.acts[v.act];
  const choices = sc?.kind === 'scene' ? sideChoices(s, id, sc) : [];
  if (sc?.kind !== 'scene' || !choices.length) {
    delete st.vote;
    return true;
  }
  const count = (c: string): number => v.votes.filter((x) => x[1] === c).length;
  const n = voters(s);
  let top = 0;
  for (const c of choices) top = imax(top, count(c.id));
  if (!force && v.votes.length < n && top * 2 <= n) return false;
  const opener = v.votes.find((x) => x[0] === v.by)?.[1];
  const pick = choices.filter((c) => count(c.id) > 0).sort((a, b) => count(b.id) - count(a.id) || (b.id === opener ? 1 : 0) - (a.id === opener ? 1 : 0))[0] ?? (choices[0] as NonNullable<SideScene['choices']>[number]);
  st.flags = [...new Set([...(st.flags ?? []), ...pick.set])];
  delete st.vote;
  event(s, now, 'side', { pid: v.by, data: { saga: id, kind: 'decided', chapter: v.chapter, choice: pick.id } });
  give(st, sc.given, v.by, now);
  advance(s, id, v.votes.map((x) => x[0]).filter((p) => findPlayer(s, p)), now);
  return true;
};

/**
 * A decision in a side saga's scene: the first choice opens the group's vote with it; a choice while it is open is a
 * vote (changing one's vote is fine until it closes). It closes by majority, with everyone, or when its day is over.
 */
export const applySideChoice = (s: CampaignState, pid: string, id: string, chapter: number, act: number, choice: string, now: Ms): CommandResult => {
  const def = sideDef(id);
  if (!def) return { ok: false, reason: 'unknownSaga' };
  const sid = def.id;
  const st = sideOf(s, sid);
  if (st.chapter !== chapter || st.act !== act) return { ok: false, reason: 'moved' };
  if (sideBlocked(s, sid)) return { ok: false, reason: 'sideClosed' };
  const sc = sideAct(s, sid);
  if (sc?.kind !== 'scene' || !sideChoices(s, sid, sc).some((c) => c.id === choice)) return { ok: false, reason: 'noChoice' };
  const m = sideMut(s, sid);
  if (!m.vote) {
    m.vote = { chapter, act, by: pid, at: now, votes: [[pid, choice]] };
    event(s, now, 'side', { pid, data: { saga: sid, kind: 'vote', chapter } });
  } else {
    const mine = m.vote.votes.find((x) => x[0] === pid);
    if (mine) mine[1] = choice;
    else m.vote.votes.push([pid, choice]);
  }
  decideSide(s, sid, now, false);
  return { ok: true };
};

/** Votes whose day is over close with the votes they have. */
export const settleSideVotes = (s: CampaignState, now: Ms): boolean => {
  let changed = false;
  for (const d of SIDES) {
    const v = s.sides?.[d.id]?.vote;
    if (v && now >= v.at + SAGA.voteMs) changed = decideSide(s, d.id, now, true) || changed;
  }
  return changed;
};

/** Closes a side saga's open vote now (a campaign whose companions are bots: they go along). */
export const closeSideVote = (s: CampaignState, id: SideId, now: Ms): boolean => decideSide(s, id, now, true);

/** Where the clues lie: off the roads, a few hexes out from the node, the ones not yet found. */
const layClues = (p: BattleParams, act: SideBattle, found: string[], seed: number): NonNullable<NonNullable<BattleParams['rules']>['clues']> => {
  const rng = seedRng(seed ^ 0xc1e5);
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const spots = [];
  for (let r = SIDE.clueRing[0]; r <= SIDE.clueRing[1]; r++) for (const h of hexRing(r)) if (!onPath.has(`${h.q},${h.r}`)) spots.push(h);
  const out: { id: string; x: number; y: number }[] = [];
  const taken: { q: number; r: number }[] = [];
  for (const id of act.clues ?? []) {
    if (found.includes(id)) continue;
    // Apart from each other, so their rings never overlap (closer only if nothing else is left).
    const apart = spots.filter((h) => taken.every((o) => hexDist(h, o) >= 2));
    const from = apart.length ? apart : spots;
    if (!from.length) continue;
    const h = from[rngInt(rng, from.length)] as (typeof spots)[number];
    spots.splice(spots.indexOf(h), 1);
    taken.push(h);
    const w = hexToWorld(h.q, h.r);
    out.push({ id, x: w.x, y: w.y });
  }
  return out;
};

/**
 * The night of lanterns: the village comes in down one road, crosses the plaza by the node and goes out by another;
 * three of them, somewhere in the line, are impostors, and Lis walks among them. The night lasts as long as the
 * crossing (the bar shows it); the waves come meanwhile.
 */
const crowdOf = (p: BattleParams, c: NonNullable<SideBattle['crowd']>, seed: number): void => {
  const rng = seedRng(seed ^ 0xface);
  const [a, b] = p.map.sectors;
  if (!a || !b) return;
  const pt = (h: { q: number; r: number }): { x: number; y: number } => hexToWorld(h.q, h.r);
  // Only the roads' last stretch: the crossing stays inside the clearing, in sight of the camera, not out on the roads.
  const inner = [...a.path.slice(-3).map(pt), pt({ q: 1, r: -1 }), pt({ q: 0, r: 1 }), pt({ q: -1, r: 1 }), ...b.path.slice(-3).reverse().map(pt)];
  const slots: number[] = [];
  for (let i = 3; i < c.people - 3; i++) slots.push(i);
  const pick = (): number => slots.splice(rngInt(rng, slots.length), 1)[0] as number;
  const impostors = c.faces.map((face) => ({ at: pick(), face }));
  const lis = pick();
  const k = SIDE.crowd;
  const rules = (p.rules ??= {});
  rules.crowd = { people: c.people, every: c.every, speed: c.speed, route: inner, impostors, lis, lantern: k.lantern, unmask: k.unmask, ticks: k.ticks };
  p.objectiveTicks = c.people * c.every + idiv(routeLength(inner), c.speed);
  delete p.objectiveQuietRate;
  delete rules.clearToFinish;
};

/** The pit: out in the woods, off the roads, as far from the node as the act says; the fight lasts until they're home. */
const rescueOf = (p: BattleParams, r: NonNullable<SideBattle['rescue']>, seed: number): void => {
  const rng = seedRng(seed ^ 0x9175);
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const spots = hexRing(r.ring).filter((h) => !onPath.has(`${h.q},${h.r}`));
  const h = spots.length ? (spots[rngInt(rng, spots.length)] as (typeof spots)[number]) : { q: r.ring, r: 0 };
  const k = SIDE.rescue;
  const rules = (p.rules ??= {});
  // The pit lies beyond the clearing, where only the roads are ground: a trail to it (the pit, the hexes round it, and a
  // way back in, the step that keeps closest to the straight line) so a hero can stand by it.
  const pw = hexToWorld(h.q, h.r);
  const trail: Hex[] = [h, ...DIRS.map((d) => ({ q: h.q + d.q, r: h.r + d.r }))];
  for (let c = h; hexLen(c.q, c.r) > p.map.bastion; ) {
    let best: Hex | null = null;
    let off = 0;
    for (const d of DIRS) {
      const n = { q: c.q + d.q, r: c.r + d.r };
      if (hexLen(n.q, n.r) >= hexLen(c.q, c.r)) continue;
      const w = hexToWorld(n.q, n.r);
      const o = Math.abs(w.x * pw.y - w.y * pw.x);
      if (!best || o < off) [best, off] = [n, o];
    }
    c = best as Hex;
    trail.push(c);
  }
  p.map = { ...p.map, trail };
  rules.rescue = { pit: pw, people: r.people, hp: r.hp, free: k.free, ticks: k.ticks, follow: k.follow, speed: k.speed, home: k.home, hurt: { ...k.hurt }, seen: k.seen };
  delete p.objectiveTicks;
  delete p.objectiveQuietRate;
  delete rules.clearToFinish;
};

/**
 * The blasting: the cracks on the clearing's rim, off the roads and spread round it; the barrels by the tower. The
 * nightmares come out of the cracks, the fight's waves over again while any is open; no extraction to end it.
 */
const blastOf = (p: BattleParams, cracks: number, seed: number): void => {
  const rng = seedRng(seed ^ 0xb1a5);
  const k = SIDE.blast;
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((x) => `${x.q},${x.r}`)));
  const rim = hexRing(p.map.bastion).filter((h) => !onPath.has(`${h.q},${h.r}`));
  const start = rngInt(rng, imax(1, rim.length));
  const at: { q: number; r: number }[] = [];
  for (let i = 0; i < cracks && rim.length; i++) at.push(rim[(start + idiv(i * rim.length, cracks)) % rim.length] as { q: number; r: number });
  const ring1 = hexRing(1);
  const stack = ring1[rngInt(rng, ring1.length)] as { q: number; r: number };
  const once = [...p.spawns];
  const span = INCURSION.extractTicks;
  const spawns = [];
  for (let round = 0; round < k.rounds; round++) for (const [i, sp] of once.entries()) if (at.length) spawns.push({ ...sp, tick: sp.tick + round * span, crack: i % at.length, at: at[i % at.length] as { q: number; r: number } });
  p.spawns = spawns;
  delete p.objectiveTicks;
  delete p.objectiveQuietRate;
  const rules = (p.rules ??= {});
  delete rules.clearToFinish;
  rules.blast = {
    stack: hexToWorld(stack.q, stack.r),
    cracks: at.map((h) => hexToWorld(h.q, h.r)),
    reach: k.reach,
    pick: k.pick,
    plant: k.plant,
    fuse: k.fuse,
    hp: k.hp,
    radius: k.radius,
    foe: k.foe,
    heroBp: k.heroBp,
    beat: k.beat,
    first: k.first,
    refill: k.refill,
    charges: k.charges,
  };
};

/**
 * The old trap: the engine near the clearing's edge, off the roads, aimed through the middle of the clearing; a winch to
 * each side of it, and the lever beside the line, halfway down it.
 */
const trapOf = (p: BattleParams, seed: number): void => {
  const rng = seedRng(seed ^ 0x7a9);
  const k = SIDE.trap;
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((x) => `${x.q},${x.r}`)));
  // A ring in from the edge, so the winches and the lever behind it stay on ground the heroes walk.
  const ring = hexRing(p.map.bastion - 1);
  const spots = ring.filter((h) => !onPath.has(`${h.q},${h.r}`) && DIRS.every((d) => !onPath.has(`${h.q + d.q},${h.r + d.r}`)));
  const h = spots.length ? (spots[rngInt(rng, spots.length)] as (typeof spots)[number]) : (ring[rngInt(rng, ring.length)] as (typeof ring)[number]);
  const at = hexToWorld(h.q, h.r);
  const len = isqrt(at.x * at.x + at.y * at.y);
  // Aimed at the node, and on across the clearing.
  const ax = -idiv(at.x * 10000, len);
  const ay = -idiv(at.y * 10000, len);
  const side = (d: number, back: number): { x: number; y: number } => ({ x: at.x + idiv(-ay * d - ax * back, 10000), y: at.y + idiv(ax * d - ay * back, 10000) });
  const rules = (p.rules ??= {});
  rules.trap = {
    at,
    ax,
    ay,
    near: k.near,
    len: mulBp(p.map.bastion * 1000, k.lenBp),
    half: k.half,
    winches: [side(k.winchOff, 0), side(-k.winchOff, 0)],
    // The lever's rope runs out beside the line, halfway down it: whoever pulls stands just off where it flies.
    lever: side(k.half + k.leverOff, -idiv(mulBp(p.map.bastion * 1000, k.lenBp), 2)),
    reach: k.reach,
    wind: k.wind,
    delay: k.delay,
    hitBp: k.hitBp,
    foe: k.foe,
    shieldBp: k.shieldBp,
    out: k.out,
  };
};

/**
 * A plunder on a side saga's field (La Quemadura 2): the incursions' own, with the field's nightmares as its waves'
 * mix and the field's first wave as its budget; the cart in the middle, the chests about.
 */
const plunderOf = (p: BattleParams, seed: number, inkBp: number): void => {
  const rng = seedRng(seed ^ 0x9a1d);
  const weights = new Map<NightmareType, number>();
  let points = 0;
  let elite: NightmareType | null = null;
  const third = idiv(INCURSION.extractTicks, SAGA.waves);
  for (const sp of p.spawns) {
    if (!(sp.kind in NIGHTMARES) || sp.boss) continue;
    const k = sp.kind as NightmareType;
    if (sp.elite) elite ??= k;
    else weights.set(k, (weights.get(k) ?? 0) + 1);
    if (sp.tick < 60 + third && !sp.elite) points += NIGHTMARES[k].points;
  }
  const entries = [...weights.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  if (!entries.length) return;
  const eliteKind = elite ?? (entries[0] as (typeof entries)[number])[0];
  p.spawns = plunderSpawns(rng, imax(1, idiv(points * 10000, PLUNDER.waveBudgetBp)), entries, eliteKind, inkBp, 0, p.map.sectors.length, INCURSION_SPAWN_SPACING);
  p.plunder = { caches: plunderCaches(seed, p.map, p.heroes.filter((h) => h.control === 'live').length) };
  p.objectiveTicks = PLUNDER.maxTicks;
  delete p.objectiveQuietRate;
  const rules = (p.rules ??= {});
  rules.mission = 'plunder';
  delete rules.clearToFinish;
};

/**
 * The collapse: the break comes from a side picked by the seed and walks across to the firm rock on the far side; the
 * scouts lie spread out on the near half, off the roads. It lasts until every scout is on the rock or lost.
 */
const collapseOf = (p: BattleParams, c: NonNullable<SideBattle['collapse']>, seed: number): void => {
  const rng = seedRng(seed ^ 0xc011);
  const k = SIDE.collapse;
  // One of the six hex directions, as a unit vector (bp).
  const d = DIRS[rngInt(rng, 6)] as (typeof DIRS)[number];
  const w = hexToWorld(d.q, d.r);
  const len = isqrt(w.x * w.x + w.y * w.y);
  const ax = idiv(w.x * 10000, len);
  const ay = idiv(w.y * 10000, len);
  // The heroes walk the clearing (and the roads): the scouts lie in it and the rock is its far side; the break comes from the field's edge.
  const reach = p.map.bastion * 1000;
  const edge = (p.map.radius - 1) * ROW;
  const along = (q: number, r: number): number => {
    const o = hexToWorld(q, r);
    return idiv(o.x * ax + o.y * ay, 10000);
  };
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((x) => `${x.q},${x.r}`)));
  const rock = -mulBp(reach, k.rockBp);
  const speed = imax(1, idiv(edge - rock, k.cross));
  const taken: { q: number; r: number }[] = [];
  const spots: { x: number; y: number; at: number }[] = [];
  const pick = (free: { q: number; r: number }[], at: number): void => {
    const far = free.filter((h) => taken.every((o) => hexDist(h, o) >= k.apart));
    const from = far.length ? far : free;
    if (!from.length) return;
    const h = from[rngInt(rng, from.length)] as { q: number; r: number };
    taken.push(h);
    spots.push({ ...hexToWorld(h.q, h.r), at });
  };
  // The first ones lie in the clearing, on the side the glass breaks from.
  const lo = mulBp(reach, k.band[0]);
  const hi = mulBp(reach, k.band[1]);
  const clearing: { q: number; r: number }[] = [];
  for (let r = 1; r <= p.map.bastion; r++) for (const h of hexRing(r)) if (!onPath.has(`${h.q},${h.r}`)) clearing.push(h);
  const near = clearing.filter((h) => along(h.q, h.r) >= lo && along(h.q, h.r) <= hi);
  // The rest come out of the haze later, out on the roads, a little ahead of the break and short of the rock.
  const roads = p.map.sectors.flatMap((sec) => sec.path);
  for (const at of k.found.slice(0, c.people)) {
    if (at === 0) {
      pick(near.filter((h) => !taken.some((o) => o.q === h.q && o.r === h.r)), 0);
      continue;
    }
    const f = edge - speed * imax(0, at - k.first);
    const open = (cells: { q: number; r: number }[]): { q: number; r: number }[] => cells.filter((h) => !taken.some((o) => o.q === h.q && o.r === h.r) && along(h.q, h.r) <= f - k.ahead && along(h.q, h.r) >= rock + k.ahead);
    // Where no road runs that way, somewhere in the clearing.
    const roadAhead = open(roads);
    const ahead = roadAhead.length ? roadAhead : open(clearing);
    // As deep as it can be: the far ones are the urgent ones.
    const deep = ahead.filter((h) => along(h.q, h.r) >= f - k.ahead * 3);
    pick(deep.length ? deep : ahead, at);
  }
  const rules = (p.rules ??= {});
  rules.collapse = {
    ax,
    ay,
    from: edge,
    rock,
    speed,
    first: k.first,
    spots,
    hp: k.hp,
    need: c.need,
    lift: k.lift,
    ticks: k.ticks,
    follow: k.follow,
    walk: k.walk,
    hurt: { ...k.hurt },
    shove: k.shove,
    burnBp: k.burnBp,
  };
  delete p.objectiveTicks;
  delete p.objectiveQuietRate;
  delete rules.clearToFinish;
};

/**
 * The drive: the herd starts out on one side of the field and the clan's pen stands across it, both off the roads;
 * the ogres come with the second and the last wave. It lasts until every boar is penned or fallen.
 */
const herdOf = (p: BattleParams, h: NonNullable<SideBattle['herd']>, seed: number): void => {
  const rng = seedRng(seed ^ 0xb0a2);
  const k = SIDE.herd;
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((x) => `${x.q},${x.r}`)));
  const spots = hexRing(k.ring).filter((x) => !onPath.has(`${x.q},${x.r}`));
  const from = spots.length ? (spots[rngInt(rng, spots.length)] as (typeof spots)[number]) : { q: k.ring, r: 0 };
  let pen = { q: -from.q, r: -from.r };
  for (const x of spots) if (hexDist(x, from) > hexDist(pen, from) || (hexDist(x, from) === hexDist(pen, from) && onPath.has(`${pen.q},${pen.r}`))) pen = x;
  const rules = (p.rules ??= {});
  rules.herd = {
    boars: h.boars,
    need: h.need,
    start: hexToWorld(from.q, from.r),
    pen: hexToWorld(pen.q, pen.r),
    penR: k.penR,
    scare: k.scare,
    guard: k.guard,
    speed: k.speed,
    drift: k.drift,
    flock: k.flock,
    apart: k.apart,
    panic: { ...k.panic },
    hp: k.hp,
    hurt: { ...k.hurt },
    lone: { ...k.lone },
    stampede: { ...k.stampede },
  };
  const last = p.spawns.reduce((m, x) => imax(m, x.tick), 0);
  for (let i = 0; i < h.ogres; i++) p.spawns.push({ tick: idiv(last * (i + 2), h.ogres + 2), sector: rngInt(rng, p.map.sectors.length), kind: 'ogre', tier: 1, elite: false });
  p.spawns.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
  delete p.objectiveTicks;
  delete p.objectiveQuietRate;
  delete rules.clearToFinish;
};

/**
 * The lash: the camp is the node, held as long as any hold; the tail swings across one axis (a side and back), the
 * mooring posts and the barrels stand around the camp off the roads, apart from each other.
 */
const lashOf = (p: BattleParams, l: NonNullable<SideBattle['lash']>, seed: number): void => {
  const rng = seedRng(seed ^ 0x1a54);
  const k = SIDE.lash;
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((x) => `${x.q},${x.r}`)));
  let spots: { q: number; r: number }[] = [];
  const taken: { q: number; r: number }[] = [];
  const lay = (n: number, apart: number): { x: number; y: number }[] => {
    const out = [];
    for (let i = 0; i < n && spots.length; i++) {
      const far = spots.filter((h) => taken.every((o) => hexDist(h, o) >= apart));
      const from = far.length ? far : spots;
      const h = from[rngInt(rng, from.length)] as { q: number; r: number };
      spots.splice(spots.indexOf(h), 1);
      taken.push(h);
      out.push(hexToWorld(h.q, h.r));
    }
    return out;
  };
  // It lasts its whole time (a quiet field doesn't hurry it): the last tilt, the big one, always comes.
  delete p.objectiveQuietRate;
  // The posts close round the camp, the barrels farther out.
  spots = hexRing(k.posts).filter((h) => !onPath.has(`${h.q},${h.r}`));
  const posts = lay(l.posts, 2);
  spots = [];
  for (let r = k.ring[0]; r <= k.ring[1]; r++) for (const h of hexRing(r)) if (!onPath.has(`${h.q},${h.r}`)) spots.push(h);
  const barrels = lay(l.barrels, 2);
  const ticks = p.objectiveTicks ?? 0;
  let total = 0;
  while (l.first + total * l.every + k.warn < ticks) total++;
  const rules = (p.rules ??= {});
  rules.lash = {
    first: l.first,
    every: l.every,
    total: imax(1, total),
    warn: k.warn,
    ticks: k.ticks,
    slide: k.slide,
    bigBp: k.bigBp,
    foeBp: k.foeBp,
    axis: rngInt(rng, 1024),
    edge: (p.map.radius - 1) * ROW,
    posts,
    postR: k.postR,
    barrels,
    barrel: { ...k.barrel },
  };
};

const DAY_MS = 86_400_000;

/** The hunt's field: the same every outing (the traps stay where they were laid), its own waves each time. */
const huntSeed = (s: CampaignState, id: SideId, st: SideState): number => deriveSeed(s.seed, 120000 + SIDES.findIndex((x) => x.id === id) * 65536 + st.chapter * 4096 + st.act * 512 + 511);

/** The Uro's places: a hex ring out from the camp, off the roads, as far from each other as they go. */
const huntPlaces = (p: BattleParams, seed: number): { q: number; r: number }[] => {
  const k = SIDE.hunt;
  const rng = seedRng(seed ^ 0x0b0e);
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((x) => `${x.q},${x.r}`)));
  const spots = hexRing(k.ring).filter((x) => !onPath.has(`${x.q},${x.r}`));
  const out: { q: number; r: number }[] = [];
  if (!spots.length) return [{ q: k.ring, r: 0 }];
  out.push(spots[rngInt(rng, spots.length)] as { q: number; r: number });
  while (out.length < k.places) {
    let best = spots[0] as { q: number; r: number };
    let bd = -1;
    for (const h of spots) {
      const d = out.reduce((m, o) => imin(m, hexDist(h, o)), 99);
      if (d > bd) {
        bd = d;
        best = h;
      }
    }
    out.push(best);
  }
  return out;
};

/** A new hunt: the Uro's whole life is set by the group (its active heroes, their level), as a saga boss's. */
const huntFresh = (s: CampaignState, act: SideBattle, tough: number, now: Ms): SideHunt => {
  const n = imax(1, s.players.filter((x) => !x.left).length);
  const hp = act.boss?.hp ?? [0];
  const base = n < hp.length ? (hp[n] as number) : mulBp(hp[hp.length - 1] as number, 10000 + (n - hp.length + 1) * 2500);
  const lv = idiv(s.players.filter((x) => !x.left).reduce((a, x) => a + x.hero.level, 0), n);
  const max = mulBp(mulBp(base, 10000 + (lv - 1) * SAGA.perLevelBp), tough);
  return { hp: max, max, place: 0, at: now, last: now, traps: [], trapId: 0, wounds: [] };
};

/**
 * The Uro as it is `now`: it heals a share a day since `at`; a week with nobody after it, it has moved to its next
 * place, its wounds halved, the traps left behind.
 */
export const huntAt = (h: SideHunt, now: Ms): SideHunt => {
  const k = SIDE.hunt;
  const days = idiv(now - h.at, DAY_MS);
  if (days <= 0) return h;
  const out = { ...h, at: h.at + days * DAY_MS };
  if (now - h.last >= k.roamDays * DAY_MS) {
    out.place = (h.place + 1) % k.places;
    out.hp = h.max - idiv(h.max - h.hp, 2);
    out.traps = [];
    out.last = now;
  } else out.hp = imin(h.max, h.hp + mulBp(h.max, k.healBp) * days);
  return out;
};

/** The hunt as the group sees it now (the bastion's panel): none before its first outing. */
export const sideHunt = (s: CampaignState, now: Ms): SideHunt | null => {
  const h = s.sides?.horn?.hunt;
  return h ? huntAt(h, now) : null;
};

/**
 * The hunt: the Uro comes out at its place with the life it has left, the traps laid for it where they were; the
 * outing lasts its time. The field is the same each outing; the nightmares come down other roads.
 */
const huntOf = (s: CampaignState, p: BattleParams, act: SideBattle, places: { q: number; r: number }[], tough: number, seed: number): void => {
  const k = SIDE.hunt;
  const h = huntAt(s.sides?.horn?.hunt ?? huntFresh(s, act, tough, s.clock), s.clock);
  const rng = seedRng(seed ^ 0x0a0a);
  for (const sp of p.spawns) if (!sp.boss) sp.sector = rngInt(rng, p.map.sectors.length);
  const at = places[h.place % places.length] as { q: number; r: number };
  const boss = p.spawns.find((x) => x.boss);
  if (boss) {
    boss.tick = 1;
    boss.at = at;
    boss.hp = h.hp;
  }
  p.spawns.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
  p.objectiveTicks = act.hunt?.out ?? 0;
  delete p.objectiveQuietRate;
  const rules = (p.rules ??= {});
  delete rules.clearToFinish;
  rules.hunt = {
    hp: h.hp,
    max: h.max,
    home: hexToWorld(at.q, at.r),
    place: h.place % places.length,
    roam: k.roam,
    walk: k.walk,
    sight: k.sight,
    still: k.still,
    wind: k.wind,
    charge: { ...k.charge },
    traps: h.traps.map((x) => ({ ...x, old: true })),
    trap: { ...k.trap },
    out: act.hunt?.out ?? 0,
    camp: k.camp,
    edge: (p.map.radius - 1) * ROW,
  };
};

/** The kit the Prestacaras fights with when it wears a hero of each class: the Unsleeping of that class. */
const COPY_KIT: Record<HeroClass, EnemyKind> = { warrior: 'corvin', ranger: 'vela', mage: 'ansel', cleric: 'lucan', artificer: 'bruni', barbarian: 'ragna', paladin: 'edda', druid: 'kaela' };

/**
 * The Prestacaras: the boss takes the faces of the group's heroes who aren't in the fight (in a fixed order), and
 * fights with the kit of their class; nobody absent, it keeps Cal's face. What the group decided shapes it: a wrong
 * accusation sends the fight to the woods, tougher, with fewer lanterns; letting Mada say goodbye makes it stand
 * still a moment, not waiting sends it in a rush.
 */
const facetakerOf = (s: CampaignState, p: BattleParams, who: string[], flags: string[], seed: number): void => {
  const k = SIDE.facetaker;
  const rules = (p.rules ??= {});
  const wrong = flags.includes('faces.wrong');
  if (wrong) {
    rules.place = 'woods';
    rules.twist = 'forest';
  }
  const sp = p.spawns.find((x) => x.boss);
  if (!sp) return;
  if (wrong && sp.hp) sp.hp = mulBp(sp.hp, k.wrongHpBp);
  const faces = s.players
    .filter((x) => !x.left && !who.includes(x.id))
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .map((x) => ({ pid: x.id, kind: COPY_KIT[x.hero.heroClass], cls: x.hero.heroClass, name: heroName(x), ...lookOf(x.hero) }));
  sp.copy = { faces, hold: flags.includes('faces.wait') ? k.holdTicks : 0, rush: flags.includes('faces.now') ? k.rushTicks : 0 };
  const first = faces[0];
  if (first) {
    let lv = 0;
    for (const pid of who) lv += findPlayer(s, pid)?.hero.level ?? 1;
    const avg = idiv(lv, imax(1, who.length));
    sp.kind = first.kind;
    sp.un = { dmgBp: mulBp(k.dmgBp, 10000 + (avg - 1) * UNSLEEPING.dmgPerLevelBp), returns: UNSLEEPING.maxReturns, copy: true };
  }
  // The lanterns, a couple of hexes out from the node, off the roads: fewer if it got away to the woods.
  const rng = seedRng(seed ^ 0x1a7e);
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const spots = [...hexRing(2), ...hexRing(3)].filter((h) => !onPath.has(`${h.q},${h.r}`));
  const at: { x: number; y: number }[] = [];
  const hexes: { q: number; r: number }[] = [];
  const worldHex = (o: { x: number; y: number }): { q: number; r: number } => hexes[at.indexOf(o)] as { q: number; r: number };
  for (let i = 0; i < (wrong ? k.lanterns.wrong : k.lanterns.right) && spots.length; i++) {
    const far = spots.filter((h) => at.every((o) => hexDist(h, worldHex(o)) >= 3));
    const from = far.length ? far : spots;
    const h = from[rngInt(rng, from.length)] as (typeof spots)[number];
    spots.splice(spots.indexOf(h), 1);
    hexes.push(h);
    at.push(hexToWorld(h.q, h.r));
  }
  rules.lanterns = { at, r: k.lanternR };
};

/**
 * The ones with your face: every hero of the group whose copy still stands (`lid.copy.<pid>` marks the felled), the
 * fighters' own first and then the absent ones', each with its class's Unsleeping kit and its player's face, going after
 * the hero its player's spectre would (the weakest or the strongest of the fighters; the nearest otherwise). They come
 * out one after another as the boss did (its life, its blows as the Prestacaras's), not with the first wave.
 */
const copiesOf = (s: CampaignState, p: BattleParams, who: string[], flags: string[]): void => {
  const k = SIDE.copies;
  const rules = (p.rules ??= {});
  const sp = p.spawns.find((x) => x.boss);
  if (!sp) return;
  p.spawns = p.spawns.filter((x) => x !== sp);
  delete rules.bossWins;
  const standing = s.players.filter((x) => !x.left && !flags.includes(`lid.copy.${x.id}`));
  const fighters = who.map((pid) => standing.find((x) => x.id === pid)).filter((x) => !!x);
  const absent = standing.filter((x) => !who.includes(x.id)).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const out = [...fighters, ...absent];
  const level = (pid: string): number => findPlayer(s, pid)?.hero.level ?? 1;
  const pick = (dir: 1 | -1): string => who.slice().sort((a, b) => dir * (level(a) - level(b)) || (a < b ? -1 : a > b ? 1 : 0))[0] ?? '';
  let lv = 0;
  for (const pid of who) lv += level(pid);
  const avg = idiv(lv, imax(1, who.length));
  rules.copies = {
    faces: out.map((x) => ({ pid: x.id, kind: COPY_KIT[x.hero.heroClass], cls: x.hero.heroClass, name: heroName(x), ...lookOf(x.hero) })),
    hunt: out.map((x) => {
      const t = x.ghostProfile?.targetPriority;
      return t === 'weakest' ? pick(1) : t === 'strongest' ? pick(-1) : '';
    }),
    hp: sp.hp ?? 1,
    dmgBp: mulBp(k.dmgBp, 10000 + (avg - 1) * UNSLEEPING.dmgPerLevelBp),
    ownBp: k.ownBp,
    first: k.first,
    gap: k.gap,
    out: k.out,
    beat: k.beat,
  };
};

/**
 * Ragna in the graveyard: the Unsleeping herself, at a share of all she does (her blows on a hero's own health too),
 * who doesn't flee. She remembers the hero
 * who made her flee before (she comes for that one). With the clan's call, she kneels at half her life.
 */
const ragnaOf = (s: CampaignState, p: BattleParams, who: string[], flags: string[]): void => {
  const k = SIDE.ragna;
  const sp = p.spawns.find((x) => x.boss);
  if (!sp) return;
  let lv = 0;
  for (const pid of who) lv += findPlayer(s, pid)?.hero.level ?? 1;
  const avg = idiv(lv, imax(1, who.length));
  const r = s.unsleeping?.ragna;
  sp.un = { dmgBp: 10000 + (avg - 1) * UNSLEEPING.dmgPerLevelBp, shareBp: k.dmgBp, returns: UNSLEEPING.maxReturns, ...(r?.hunt && who.includes(r.hunt) ? { hunt: r.hunt } : {}) };
  if (flags.includes('horn.call')) (p.rules ??= {}).call = { atBp: k.callBp, kneel: k.kneel };
};

/**
 * What comes up: Bruni at the bastion's forge, among `towers` of its towers spread round the node off the roads. He
 * doesn't flee and hits at a share of his strength; he remembers the hero who made him flee before. The rails brought
 * down: fewer nightmares with him. Left open: he is tougher, and Dorn's automatons come up behind him. If he already
 * fell elsewhere, the boss is the biggest automaton he turned (with the same rule for the towers).
 */
const risingOf = (s: CampaignState, p: BattleParams, who: string[], flags: string[]): void => {
  const k = SIDE.rising;
  const open = flags.includes('rails.open');
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const spots = hexRing(k.ring).filter((h) => !onPath.has(`${h.q},${h.r}`));
  p.structures = [];
  for (let i = 0; i < k.towers && spots.length; i++) {
    const h = spots[idiv(i * spots.length, k.towers)] as Hex;
    p.structures.push({ type: k.tower[i % k.tower.length] as (typeof k.tower)[number], level: k.level, material: k.material, q: h.q, r: h.r });
  }
  const rules = (p.rules ??= {});
  const hpBp = rules.enemyHpBp ?? 10000;
  rules.rising = { first: k.first, every: k.every, mark: k.mark, guard: k.guard, turn: k.turn, range: k.range, arc: k.arc, weakBp: k.weakBp };
  if (open) rules.rising.dorn = { ...k.dorn, n: k.dorn.n + (imax(1, who.length) - 1) * k.dorn.more, hp: mulBp(k.dorn.hp, hpBp), foe: mulBp(k.dorn.foe, hpBp) };
  // The rails brought down behind him: what came after him stays below (a share of the nightmares, spread evenly).
  else {
    let kept = 0;
    p.spawns = p.spawns.filter((sp, i) => {
      if (sp.boss) return true;
      const keep = idiv((i + 1) * k.closedBp, 10000) > kept;
      if (keep) kept++;
      return keep;
    });
  }
  const sp = p.spawns.find((x) => x.boss);
  if (!sp) return;
  if (open && sp.hp) sp.hp = mulBp(sp.hp, k.openHpBp);
  if (flags.includes('rails.felled')) {
    sp.kind = 'hulk';
    sp.elite = false;
    return;
  }
  let lv = 0;
  for (const pid of who) lv += findPlayer(s, pid)?.hero.level ?? 1;
  const avg = idiv(lv, imax(1, who.length));
  const b = s.unsleeping?.bruni;
  sp.un = { dmgBp: 10000 + (avg - 1) * UNSLEEPING.dmgPerLevelBp, shareBp: k.shareBp, returns: UNSLEEPING.maxReturns, ...(b?.hunt && who.includes(b.hunt) ? { hunt: b.hunt } : {}) };
};

/**
 * The ink in the water: the houses on the square's rim off the roads, spread round; the mill's stone up one of the
 * roads. Everything climbs out of the well (round the node): nothing that flies or goes
 * for buildings, and the waves come `rounds` times until the well is sealed.
 */
const wellOf = (p: BattleParams, seed: number): void => {
  const k = SIDE.well;
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const free = (n: number): Hex[] => hexRing(n).filter((h) => !onPath.has(`${h.q},${h.r}`));
  const rim = free(k.ring);
  const houses = [];
  for (let i = 0; i < k.houses && rim.length; i++) houses.push(hexToWorld((rim[idiv(i * rim.length, k.houses)] as Hex).q, (rim[idiv(i * rim.length, k.houses)] as Hex).r));
  // The mill is up one of the roads (the clearing's rim is all houses): the stone rolls down it into the square.
  const rng = seedRng(seed ^ 0x3e11);
  const road = p.map.sectors[rngInt(rng, p.map.sectors.length)]?.path ?? [];
  const from = imax(0, road.findIndex((h) => hexLen(h.q, h.r) === k.mill));
  // Down the road to the square, then straight on to the well.
  const route = road.slice(from).map((h) => hexToWorld(h.q, h.r));
  route.push({ x: 0, y: 0 });
  const mouth = hexRing(1);
  p.spawns = p.spawns.filter((sp) => !NIGHTMARES[sp.kind as NightmareType]?.flying && !NIGHTMARES[sp.kind as NightmareType]?.siege && !NIGHTMARES[sp.kind as NightmareType]?.structureHunter && !NIGHTMARES[sp.kind as NightmareType]?.shootsTowers);
  for (const sp of p.spawns) sp.at = mouth[rngInt(rng, mouth.length)] as Hex;
  const rules = (p.rules ??= {});
  rules.well = { houses, hp: k.hp, guard: k.guard, door: k.door, mill: route[0] as { x: number; y: number }, route, stoneAt: k.stoneAt, reach: k.reach, slow: k.slow, fast: k.fast, seal: k.seal, lose: k.lose, beat: k.beat };
  rounds(p, k.rounds);
};

/**
 * Fair day: the stalls round the well, off the roads; the field's nightmares (none that fly or go for buildings) with
 * the draggers and the ink thieves spread through its time, which it always runs.
 */
const fairOf = (p: BattleParams, n: number, seed: number): void => {
  const k = SIDE.fair;
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const ring = hexRing(k.ring).filter((h) => !onPath.has(`${h.q},${h.r}`));
  const stalls = [];
  for (let i = 0; i < k.stalls && ring.length; i++) {
    const h = ring[idiv(i * ring.length, k.stalls)] as Hex;
    stalls.push({ ...hexToWorld(h.q, h.r), wares: k.wares });
  }
  const rng = seedRng(seed ^ 0xfa1e);
  p.spawns = p.spawns.filter((sp) => {
    const d = NIGHTMARES[sp.kind as NightmareType];
    return !d?.flying && !d?.siege && !d?.structureHunter && !d?.shootsTowers;
  });
  const drag = k.draggers + (imax(1, n) - 1) * k.more;
  for (let i = 0; i < drag; i++) p.spawns.push({ tick: 60 + idiv(i * (k.out - k.calm), drag), sector: rngInt(rng, p.map.sectors.length), kind: 'dragger', tier: 1, elite: false });
  for (let i = 0; i < k.thieves; i++) p.spawns.push({ tick: k.thiefAt + idiv(i * (k.out - k.calm - k.thiefAt), k.thieves), sector: rngInt(rng, p.map.sectors.length), kind: 'stalker', tier: 1, elite: false });
  p.spawns.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
  delete p.objectiveTicks;
  delete p.objectiveQuietRate;
  const rules = (p.rules ??= {});
  delete rules.clearToFinish;
  rules.fair = { stalls, cart: 0, reach: k.reach, lift: k.lift, dragBp: k.dragBp, light: k.light, lantern: k.lantern, out: k.out, beat: k.beat };
};

/**
 * The low water: one road (the longest) is the causeway, and the reef with the net is its far end; the way out to sea
 * runs from the node towards it. The field's nightmares come down the other roads (none that fly); the Drowned climb out
 * of the water once it comes back.
 */
const tideOf = (p: BattleParams): void => {
  const k = SIDE.tide;
  let road = 0;
  p.map.sectors.forEach((sec, i) => {
    if (sec.path.length > (p.map.sectors[road]?.path.length ?? 0)) road = i;
  });
  const far = p.map.sectors[road]?.path[0] ?? { q: 6, r: 0 };
  const reef = hexToWorld(far.q, far.r);
  const len = imax(1, isqrt(reef.x * reef.x + reef.y * reef.y));
  const dir = { x: idiv(reef.x * 1000, len), y: idiv(reef.y * 1000, len) };
  const others = p.map.sectors.map((_, i) => i).filter((i) => i !== road);
  p.spawns = p.spawns
    .filter((sp) => !NIGHTMARES[sp.kind as NightmareType]?.flying)
    .map((sp, i) => (sp.sector === road && others.length ? { ...sp, sector: others[i % others.length] as number } : sp))
    // They come with the water: the low water itself is quiet.
    .map((sp) => ({ ...sp, tick: sp.tick + k.calm }));
  delete p.objectiveTicks;
  delete p.objectiveQuietRate;
  const rules = (p.rules ??= {});
  delete rules.clearToFinish;
  // The stilt-houses stand longer than a clearing's node: the night is long.
  p.keepHp = p.keepMaxHp = mulBp(p.keepMaxHp, k.nodeBp);
  rules.tide = { ...k, drowned: { ...k.drowned }, reef, dir, start: len + k.start, shore: k.shore };
  rules.drowned = { ...SIDE.drowned };
};

/**
 * The fin: the sea is on the side of the clearing away from its roads (the way in is from there towards the node);
 * three lanes along that way, posts on each, and the Drowned climb out on the clearing's rim on the sea side.
 */
const finOf = (p: BattleParams, nodeBp: number = SIDE.fin.nodeBp): void => {
  const k = SIDE.fin;
  let gx = 0;
  let gy = 0;
  for (const sec of p.map.sectors) {
    const g = hexToWorld(sec.gate.q, sec.gate.r);
    gx += g.x;
    gy += g.y;
  }
  // The sea faces away from the roads' mouths (towards the node from there is the way in).
  const len = imax(1, isqrt(gx * gx + gy * gy));
  const dir = gx || gy ? { x: idiv(gx * 1000, len), y: idiv(gy * 1000, len) } : { x: 1000, y: 0 };
  const at = (along: number, off: number): { x: number; y: number } => ({ x: idiv(dir.x * along - dir.y * off, 1000), y: idiv(dir.y * along + dir.x * off, 1000) });
  const posts = k.lanes.flatMap((off) => k.posts.map((a) => at(a, off)));
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const shore = hexRing(p.map.bastion)
    .filter((h) => !onPath.has(`${h.q},${h.r}`))
    .map((h) => hexToWorld(h.q, h.r))
    .filter((w) => w.x * dir.x + w.y * dir.y < 0);
  // A road whose mouth is on the sea side is under water: what would come down it comes down the others.
  const land = p.map.sectors.map((sec, i) => ({ i, g: hexToWorld(sec.gate.q, sec.gate.r) })).filter((x) => x.g.x * dir.x + x.g.y * dir.y >= 0).map((x) => x.i);
  p.spawns = p.spawns
    .filter((sp) => !NIGHTMARES[sp.kind as NightmareType]?.flying)
    .map((sp, i) => (land.length && !land.includes(sp.sector) ? { ...sp, sector: land[i % land.length] as number } : sp));
  delete p.objectiveTicks;
  delete p.objectiveQuietRate;
  const rules = (p.rules ??= {});
  delete rules.clearToFinish;
  // The stilt-houses again: they stand the long night.
  p.keepHp = p.keepMaxHp = mulBp(p.keepMaxHp, nodeBp);
  rules.fin = { ...k, lanes: [...k.lanes], posts, dir, shore, drowned: { ...k.drowned } };
  rules.drowned = { ...SIDE.drowned };
};

/**
 * The dragon in the water (La Orilla 5): the fin's lanes and shore, with its fire down the lanes instead of the wave,
 * the Drowned in batches, and a longer night with the heart (whose carrier the fire follows).
 */
const breathOf = (p: BattleParams, n: number, flags: string[]): void => {
  const k = SIDE.breath;
  finOf(p, k.nodeBp);
  const rules = p.rules ?? {};
  const f = rules.fin;
  if (!f) return;
  const heart = flags.includes('orilla.corazon');
  rules.fin = { ...f, first: k.first, every: k.every, everyNight: k.everyNight, warn: k.warn, hitBp: k.hitBp, night: k.night, dawn: heart ? k.heartDawn : k.dawn, calm: k.calm, drowned: { every: k.drowned.every, hp: k.drowned.hp, batch: k.drowned.batch[imin(n, k.drowned.batch.length - 1)] ?? 1 }, breath: true };
  if (heart) rules.sunken = { heart: { ...k.heart } };
};

/**
 * The Drowned Dragon (La Orilla 5): the fin's lanes and wave again, no dawn; the dragon rises at the clearing's rim
 * furthest out to sea, Yorra's boats spread along that shore. Its life as a saga boss's (party, level), and more with
 * its heart gone to the forge (and steam for fire).
 */
const sunkenOf = (s: CampaignState, p: BattleParams, who: string[], flags: string[]): void => {
  const k = SIDE.sunken;
  finOf(p, k.nodeBp);
  const rules = p.rules ?? {};
  const f = rules.fin;
  if (!f) return;
  rules.fin = { ...f, first: k.fin.first, every: k.fin.every, everyNight: k.fin.every, warn: k.fin.warn, night: 0, dawn: k.fin.dawn, calm: 0, drowned: { ...k.drowned } };
  const sea = (w: { x: number; y: number }): number => -(w.x * f.dir.x + w.y * f.dir.y);
  const shore = f.shore.slice().sort((a, b) => sea(b) - sea(a) || a.x - b.x || a.y - b.y);
  const at = shore[0] ?? { x: 0, y: 0 };
  const boats = [];
  for (let i = 0; i < k.boats && f.shore.length; i++) boats.push(f.shore[idiv(i * f.shore.length + idiv(f.shore.length, 2), k.boats)] as { x: number; y: number });
  let lv = 0;
  for (const pid of who) lv += findPlayer(s, pid)?.hero.level ?? 1;
  const n = imax(1, who.length);
  const heart = flags.includes('orilla.corazon');
  const base = k.hp[imin(n, k.hp.length - 1)] ?? k.hp[1] ?? 0;
  const hp = mulBp(mulBp(mulBp(base, 10000 + (idiv(lv, n) - 1) * SAGA.perLevelBp), n >= 4 ? 11000 : 10000), heart ? k.heartBp : 10000);
  rules.sunken = {
    ...(heart ? { heart: { ...SIDE.breath.heart } } : {}),
    boss: { at: { x: at.x, y: at.y }, rise: k.rise, hp, takenBp: k.takenBp, crushBp: k.crushBp, steam: heart, breath: { ...k.breath }, boats, harpoon: { ...k.harpoon }, beat: k.beat },
  };
};

/**
 * The Maw: the sea on the side away from the roads' mouths, as the fin's; nothing comes down the roads, only the
 * Drowned out of the sea. The Maw at the clearing's rim there, its stalk towards the land, the braziers round the lighthouse on the sea side, the
 * Drowned climbing out on that rim. Its life as a saga boss's: by party and level.
 */
const mawOf = (s: CampaignState, p: BattleParams, who: string[]): void => {
  const k = SIDE.maw;
  let gx = 0;
  let gy = 0;
  for (const sec of p.map.sectors) {
    const g = hexToWorld(sec.gate.q, sec.gate.r);
    gx += g.x;
    gy += g.y;
  }
  const len = imax(1, isqrt(gx * gx + gy * gy));
  const dir = gx || gy ? { x: idiv(gx * 1000, len), y: idiv(gy * 1000, len) } : { x: 1000, y: 0 };
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const rim = hexRing(p.map.bastion).filter((h) => !onPath.has(`${h.q},${h.r}`));
  const sea = (h: Hex): number => {
    const w = hexToWorld(h.q, h.r);
    return -(w.x * dir.x + w.y * dir.y);
  };
  const shore = rim.filter((h) => sea(h) > 0).map((h) => hexToWorld(h.q, h.r));
  const atHex = rim.slice().sort((a, b) => sea(b) - sea(a))[0] ?? { q: p.map.bastion, r: 0 };
  const at = hexToWorld(atHex.q, atHex.r);
  const al = imax(1, isqrt(at.x * at.x + at.y * at.y));
  const stalkAt = { x: at.x - idiv(at.x * k.stalk, al), y: at.y - idiv(at.y * k.stalk, al) };
  const ring = hexRing(k.ring).filter((h) => !onPath.has(`${h.q},${h.r}`) && sea(h) >= 0);
  const braziers = [];
  for (let i = 0; i < k.braziers && ring.length; i++) {
    const h = ring[idiv(i * ring.length, k.braziers)] as Hex;
    braziers.push(hexToWorld(h.q, h.r));
  }
  // Nothing comes down the roads tonight: what comes, comes out of the sea.
  p.spawns = [];
  delete p.objectiveTicks;
  delete p.objectiveQuietRate;
  const rules = (p.rules ??= {});
  delete rules.clearToFinish;
  p.keepHp = p.keepMaxHp = mulBp(p.keepMaxHp, k.nodeBp);
  const hpBp = rules.enemyHpBp ?? 10000;
  let lv = 0;
  for (const pid of who) lv += findPlayer(s, pid)?.hero.level ?? 1;
  const n = imax(1, who.length);
  const base = k.hp[imin(n, k.hp.length - 1)] ?? k.hp[1] ?? 0;
  const hp = mulBp(mulBp(base, 10000 + (idiv(lv, n) - 1) * SAGA.perLevelBp), n >= 4 ? 11000 : 10000);
  rules.maw = { at, rise: k.rise, hp, stalkAt, stalkHp: mulBp(k.stalkHp, hpBp), sight: k.sight, pull: k.pull, braziersBp: k.braziersBp, litBp: k.litBp, darkBp: k.darkBp, dark: k.dark, bite: { ...k.bite }, braziers, reach: k.reach, douse: k.douse, relight: k.relight, shore, drowned: { ...k.drowned }, beat: k.beat };
  rules.drowned = { ...SIDE.drowned };
};

/** How big the fair turned out (the wares back at their stalls): the mark the closing scene and the banner read. */
const fairApply = (st: SideState, battle: BattleState): void => {
  const w = battle.fair?.wares ?? [];
  const pct = w.length ? idiv(w.filter((x) => x.at === 'stall').length * 10000, w.length) : 0;
  const size = pct >= SIDE.fair.big ? 'grande' : pct >= SIDE.fair.mid ? 'mediana' : 'chica';
  st.flags = [...(st.flags ?? []).filter((f) => !f.startsWith('pozo.feria.')), `pozo.feria.${size}`, 'pozo.agua'];
};

/** The rails left open: ore comes up them into the storage every Marea after the chapter (from campaign/resolve.ts). */
export const risingOre = (s: CampaignState, now: Ms): void => {
  const st = s.sides?.rails;
  if (!st?.done[3] || !(st.flags ?? []).includes('rails.open')) return;
  addRes(s.storage, SIDE.rising.ore);
  event(s, now, 'side', { data: { saga: 'rails', kind: 'ore' } });
};

/** The runaway carts: the escort's train runs on rails with no brake (battle/rails.ts); its barricades are as tough as its nightmares, and tougher for a bigger party. */
const railsOf = (p: BattleParams, n: number): void => {
  const rules = (p.rules ??= {});
  const k = SIDE.rails;
  // More hands break them quicker: they get tougher with every hero past the first.
  const hpBp = mulBp(rules.enemyHpBp ?? 10000, 10000 + (imax(1, n) - 1) * k.partyBp);
  rules.rails = { ...k, speed: [k.speed[0], k.speed[1]], hp: mulBp(k.hp, hpBp), weight: mulBp(k.weight, hpBp) };
};

/**
 * The alarm bells: three rooms on the clearing's rim, each as far from the roads' mouths and the other rooms as it can
 * be, a lever at each and its guards in a short arc on the node's side of it (more of them for a bigger party). The
 * waves come round `rounds` times; there is no time to hold out, only the levers.
 */
const bellsOf = (p: BattleParams, n: number): void => {
  const k = SIDE.bells;
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((x) => `${x.q},${x.r}`)));
  const rim = hexRing(p.map.bastion).filter((h) => !onPath.has(`${h.q},${h.r}`));
  const taken: { q: number; r: number }[] = p.map.sectors.map((sec) => sec.gate);
  const levers: { q: number; r: number }[] = [];
  for (let i = 0; i < k.rooms && rim.length; i++) {
    let best = rim[0] as { q: number; r: number };
    let bd = -1;
    for (const h of rim) {
      let d = 0x7fffffff;
      for (const o of taken) d = imin(d, hexDist(h, o));
      if (d > bd) {
        bd = d;
        best = h;
      }
    }
    levers.push(best);
    taken.push(best);
  }
  const rules = (p.rules ??= {});
  const hpBp = rules.enemyHpBp ?? 10000;
  const guards = k.guards + (imax(1, n) - 1) * k.more;
  rules.bells = {
    rooms: levers.map((h) => {
      const lever = hexToWorld(h.q, h.r);
      const len = imax(1, isqrt(lever.x * lever.x + lever.y * lever.y));
      // Inward of the lever, side by side across the way to it.
      const ix = idiv(lever.x * 1000, len);
      const iy = idiv(lever.y * 1000, len);
      const posts = [];
      for (let g = 0; g < guards; g++) {
        const side = (g * 2 - (guards - 1)) * 450;
        posts.push({ x: lever.x - ix + idiv(iy * side, 1000), y: lever.y - iy - idiv(ix * side, 1000) });
      }
      return { lever, posts };
    }),
    hp: mulBp(k.hp, hpBp),
    damage: k.damage,
    foe: mulBp(k.foe, hpBp),
    every: k.every,
    radius: k.radius,
    aggro: k.aggro,
    taunt: k.taunt,
    step: k.step,
    pull: k.pull,
    reach: k.reach,
    beat: k.beat,
  };
  rounds(p, k.rounds);
};

/**
 * Hanging the bell: the winches round the frame, `ring` hexes out, off the roads and as far from their mouths and each
 * other as they can be; a turn of one for each hero (up to the winches); the waves come round (the old frame's life is
 * the Order's post's, `neck`).
 */
const winchOf = (p: BattleParams): void => {
  const k = SIDE.winch;
  const onPath = new Set(p.map.sectors.flatMap((sec) => sec.path.map((x) => `${x.q},${x.r}`)));
  const ring = hexRing(k.ring).filter((h) => !onPath.has(`${h.q},${h.r}`));
  const taken: { q: number; r: number }[] = p.map.sectors.map((sec) => sec.path[sec.path.length - 1] ?? sec.gate);
  const at: { q: number; r: number }[] = [];
  for (let i = 0; i < k.winches && ring.length; i++) {
    let best = ring[0] as { q: number; r: number };
    let bd = -1;
    for (const h of ring) {
      let d = 0x7fffffff;
      for (const o of taken) d = imin(d, hexDist(h, o));
      if (d > bd) {
        bd = d;
        best = h;
      }
    }
    at.push(best);
    taken.push(best);
  }
  const n = imin(p.heroes.length, at.length);
  (p.rules ??= {}).winch = { at: at.map((h) => hexToWorld(h.q, h.r)), reach: k.reach, need: k.need * imax(1, n), sink: k.sink, beat: k.beat };
  rounds(p, k.rounds);
};

/**
 * The night of the bell at the bastion: the fight is on the group's own bastion, as a Marea's wave is (its works
 * standing, everyone else's spectre at their post), with the heroes who play it there live. The wave is the last of the
 * Marea the group is at, `budgetBp` (dusk) or `dawnBp` (dawn) bigger; at dusk the Hollow Shell comes with it (and the Ash
 * Witch, if Maelis isn't coming), at dawn no boss. A boss of the Marea's own stays below.
 */
const bastionOf = (s: CampaignState, act: SideBattle, who: string[]): BattleParams => {
  const k = SIDE.night;
  const dusk = act.bastion === 'dusk';
  const p = waveBattleParams(s, s.clock, { live: who, marea: s.mareaIndex, wave: WAVES_PER_MAREA - 1, byLevel: false });
  const rng = seedRng(p.seed ^ 0xb311);
  const sectors = imax(1, p.map.sectors.length);
  const rest = p.spawns.filter((sp) => !(sp.kind in BOSSES));
  // Bigger than any: a share of the wave comes twice, a moment after.
  const more = (dusk ? k.budgetBp : k.dawnBp) - 10000;
  let added = 0;
  const spawns = [...rest];
  rest.forEach((sp, i) => {
    if (idiv((i + 1) * more, 10000) <= added) return;
    added++;
    spawns.push({ ...sp, tick: sp.tick + 6 });
  });
  p.bossHp = 0;
  if (dusk) {
    const activity = imax(10000, p.heroes.reduce((a, h) => a + h.powerBp, 0));
    const last = rest.reduce((a, sp) => imax(a, sp.tick), 0);
    const at = idiv(last, 3);
    const sector = rngInt(rng, sectors);
    spawns.push({ tick: at, sector, kind: 'shell', tier: 2, elite: false, hp: mulBp(bossHp('shell', activity, s.mareaIndex), k.shellBp), boss: true });
    if (!turtleMark(s, 'maelis.knows')) spawns.push({ tick: at + 240, sector: (sector + 1) % sectors, kind: 'witch', tier: 2, elite: false, hp: mulBp(bossHp('witch', activity, s.mareaIndex), k.witchBp) });
  }
  p.spawns = spawns.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
  delete p.plan;
  const rules = (p.rules ??= {});
  rules.night = true;
  alliesOf(s, p, who, dusk);
  return p;
};

/**
 * Who comes to the night of the bell: the ones the group earned (NIGHT_ALLIES), at its level and SIDE.night.powerBp,
 * each to a post round the bastion and in at the gate, up a road, or over the land's edge. At dawn, the ones who came
 * at dusk are there from the start.
 */
const alliesOf = (s: CampaignState, p: BattleParams, who: string[], dusk: boolean): void => {
  const k = SIDE.night;
  let lv = 0;
  for (const pid of who) lv += findPlayer(s, pid)?.hero.level ?? 1;
  const level = idiv(lv, imax(1, who.length));
  const sectors = p.map.sectors;
  const edge = hexRing(imax(1, p.map.radius - k.inset));
  const earned = NIGHT_ALLIES.filter((a) => allyComes(s, a));
  const hero = (a: NightAlly, i: number) => ({ pid: `npc:${a.id}`, name: a.name, cls: a.cls, level, powerBp: k.powerBp, post: i % imax(1, sectors.length) });
  if (!dusk) for (const [i, a] of earned.filter((x) => x.fight === 'dusk').entries()) p.heroes.push({ ...hero(a, i), control: 'ghost' });
  const come = earned.filter((a) => a.fight === (dusk ? 'dusk' : 'dawn'));
  if (!come.length) return;
  (p.rules ??= {}).allies = {
    come: come.map((a, i) => {
      const sec = sectors[i % imax(1, sectors.length)];
      const h = a.from === 'gate' && sec ? sec.gate : a.from === 'road' && sec?.path[0] ? sec.path[0] : (edge[idiv(i * edge.length, come.length)] ?? { q: 0, r: 0 });
      const at = hexToWorld(h.q, h.r);
      return { ...hero(a, i), x: at.x, y: at.y };
    }),
    at: [...k.at],
    hurtBp: [...k.hurtBp],
    blow: { r: k.blow.r, foe: mulBp(k.blow.foe, p.rules?.enemyHpBp ?? 10000), stun: k.blow.stun },
  };
};

/** Whether an ally of the night of the bell comes: the Turtle done with their mark, their saga done, or its mark. */
const allyComes = (s: CampaignState, a: NightAlly): boolean => {
  if (a.turtle) return turtleMark(s, a.turtle);
  if (!a.side) return false;
  return a.flag ? sideFlags(s, a.side).includes(a.flag) : sideDone(s, a.side);
};

/** The bell at midnight: its rings and its push (battle/toll.ts); the field's waves come round. */
const tollOf = (p: BattleParams): void => {
  const k = SIDE.toll;
  (p.rules ??= {}).toll = { first: k.first, ring: [k.ring[0], k.ring[1]], step: k.step, window: k.window, miss: k.miss, reach: k.reach, radius: k.radius, push: k.push, stun: k.stun, need: k.need, beat: k.beat };
  rounds(p, k.rounds);
};

/** The field's waves, `n` times over, with no time to hold out: the act's own rule decides it. */
const rounds = (p: BattleParams, n: number): void => {
  const once = [...p.spawns];
  p.spawns = [];
  for (let round = 0; round < n; round++) for (const sp of once) p.spawns.push({ ...sp, tick: sp.tick + round * INCURSION.extractTicks });
  delete p.objectiveTicks;
  delete p.objectiveQuietRate;
  if (p.rules) delete p.rules.clearToFinish;
};

/** The five strikes: the Anvil is the node; its wave burns as hard as the nightmares are tough (battle/anvil.ts). */
const anvilOf = (p: BattleParams): void => {
  const k = SIDE.anvil;
  const rules = (p.rules ??= {});
  rules.anvil = { first: k.first, ring: [k.ring[0], k.ring[1]], step: k.step, rest: k.rest, window: k.window, miss: k.miss, reach: k.reach, radius: k.radius, foe: mulBp(k.foe, rules.enemyHpBp ?? 10000), stun: k.stun, need: k.need, beat: k.beat };
  rounds(p, k.rounds);
};

const routeLength = (r: { x: number; y: number }[]): number => {
  let d = 0;
  for (let i = 1; i < r.length; i++) {
    const a = r[i - 1] as { x: number; y: number };
    const b = r[i] as { x: number; y: number };
    d += isqrt(dist2(a.x, a.y, b.x, b.y));
  }
  return d;
};

/**
 * A finished side chapter that pays a unique piece can be played again (docs/UNICAS.md §1, «Repeticiones»: La Orilla 5,
 * by the Turtle's rules): its last fight, for whoever came late or changed class. Its index, or -1.
 */
export const sideReplayAct = (id: SideId, chapter: number): number => {
  const ch = sideDef(id)?.chapters[chapter];
  return ch?.unique ? ch.acts.map((a) => a.kind).lastIndexOf('battle') : -1;
};

/** Whether a hero still has something to take from playing a finished side chapter again (its piece, the dragon's seal). */
export const sideReplayPays = (s: CampaignState, pid: string, id: SideId, chapter: number): boolean => {
  const ch = sideDef(id)?.chapters[chapter];
  const p = findPlayer(s, pid);
  if (!ch?.unique || !p) return false;
  return !hasUnique(p, ch.unique) || (sideReplayAct(id, chapter) >= 0 && ch.acts.some((a) => a.kind === 'battle' && !!a.sunken) && !(p.hero.seals?.dragon ?? 0));
};

/** The fight for the act a side saga is at (or, `replay`, a finished chapter's last fight played again). */
export const sideBattleParams = (s: CampaignState, pids: string[], id: SideId, replay?: number): { params: BattleParams; ticket: SagaTicket } | null => {
  const again = replay !== undefined && replay < sideOf(s, id).chapter && sideReplayAct(id, replay) >= 0;
  if (replay !== undefined && !again) return null;
  const act = again ? (sideDef(id)?.chapters[replay as number]?.acts[sideReplayAct(id, replay as number)] ?? null) : sideAct(s, id);
  const who = party(s, pids);
  if (!act || act.kind !== 'battle' || !who.length || (!again && sideBlocked(s, id))) return null;
  const st0 = sideOf(s, id);
  const st = again ? { ...st0, chapter: replay as number, act: sideReplayAct(id, replay as number), clues: [] as SideState['clues'] } : st0;
  const seed = deriveSeed(s.seed, 120000 + SIDES.findIndex((x) => x.id === id) * 65536 + st.chapter * 4096 + st.act * 512 + (st.tries % 512) + (again ? 300000 : 0));
  const tough = (again ? sideDef(id)?.chapters[st.chapter] : sideChapter(s, id))?.toughBp ?? 10000;
  const flags = sideFlags(s, id);
  // Ragna already fell elsewhere: no Ragna in the graveyard, only the nightmares round it to hold off.
  const felled = !!act.ragna && flags.includes('horn.felled');
  const fight: SideBattle = felled ? { ...act, mode: 'hold' } : act;
  if (felled) delete fight.boss;
  // The hunt's field stays put between outings (its own waves come with the outing's seed).
  const p = act.bastion ? bastionOf(s, fight, who) : act.mode === 'descent' ? descent(s, fight, who, seed, tough) : field(s, fight, who, act.hunt ? huntSeed(s, id, st) : seed, tough);
  p.seed = seed;
  const rules = (p.rules ??= {});
  if (act.place) rules.place = act.place;
  if (act.night) rules.night = true;
  const clues = layClues(p, act, st.clues.map((c) => c.id), seed);
  if (clues.length) rules.clues = clues;
  if (act.crowd) crowdOf(p, act.crowd, seed);
  if (act.rescue) rescueOf(p, act.rescue, seed);
  if (act.facetaker) facetakerOf(s, p, who, flags, seed);
  if (act.ragna && !felled) ragnaOf(s, p, who, flags);
  if (act.herd) herdOf(p, act.herd, seed);
  if (act.lash) lashOf(p, act.lash, seed);
  if (act.collapse) collapseOf(p, act.collapse, seed);
  if (act.plunder) plunderOf(p, seed, act.inkBp ?? 0);
  if (act.trap) trapOf(p, seed);
  if (act.blast) blastOf(p, act.blast.cracks, seed);
  if (act.rails) railsOf(p, who.length);
  if (act.bells) bellsOf(p, who.length);
  if (act.anvil) anvilOf(p);
  if (act.rising) risingOf(s, p, who, flags);
  if (act.well) wellOf(p, seed);
  if (act.fair) fairOf(p, who.length, seed);
  if (act.tide) tideOf(p);
  if (act.fin) finOf(p);
  if (act.maw) mawOf(s, p, who);
  if (act.breath) breathOf(p, who.length, flags);
  if (act.sunken) sunkenOf(s, p, who, flags);
  if (act.neck) {
    const k = SIDE.neck;
    rules.neck = { open: k.open, shut: k.shut, squeezeBp: k.squeezeBp, shake: { ...k.shake } };
    // The Order's post: its old walls still stand round the camp.
    if (act.mode !== 'descent') p.keepHp = p.keepMaxHp = mulBp(p.keepMaxHp, k.postBp);
  }
  if (act.climb) {
    const k = SIDE.climb;
    rules.climb = { ...k, pace: [...k.pace], boars: flags.includes('lid.boars') ? k.boars : 0 };
  }
  if (act.winch) winchOf(p);
  if (act.copies) copiesOf(s, p, who, flags);
  if (act.toll) tollOf(p);
  if (act.flight && p.dungeon) {
    // The way back: every room behind them already, the sea coming in (with the heart, if they took it).
    const k = SIDE.flight;
    p.dungeon.spawns = [];
    rules.drinker = {
      ...SIDE.drinker,
      spit: { ...SIDE.drinker.spit },
      flood: { delay: k.delay, speed: k.speed, wadeBp: k.wadeBp, drownBp: k.drownBp },
      beat: k.beat,
      up: true,
      drowned: { ...k.drowned },
      ...(flags.includes('orilla.corazon') ? { heart: { ...k.heart } } : {}),
    };
    rules.drowned = { ...SIDE.drowned };
  }
  if (act.drinker) rules.drinker = { ...SIDE.drinker, spit: { ...SIDE.drinker.spit }, flood: { ...SIDE.drinker.flood } };
  if (act.hunt) huntOf(s, p, act, huntPlaces(p, huntSeed(s, id, st)), tough, seed);
  return { params: snapshot(p), ticket: { chapter: st.chapter, act: st.act, side: id, ...(again ? { replay: true } : {}) } };
};

/**
 * A side saga's fight, re-simulated: the clues picked up go on the board (won or not); a win moves the saga on
 * and pays the fighters' XP. A ticket the saga already moved past only pays the XP.
 */
export const applySideBattle = (s: CampaignState, who: string[], ticket: SagaTicket, battle: BattleState, now: Ms): SagaResult => {
  const id = ticket.side as SideId;
  const won = battle.result === 'won';
  if (!sideDef(id)) return { counted: false, won };
  // A finished chapter played again (La Orilla 5): won, its piece for whoever lacks it, and the dragon's seal if missing.
  if (ticket.replay) {
    const ch = sideDef(id)?.chapters[ticket.chapter];
    let unique: number | undefined;
    if (won && ch?.unique)
      for (const pid of who) {
        const p = findPlayer(s, pid);
        if (!p) continue;
        if (!hasUnique(p, ch.unique)) {
          if (p.hero.level >= (ch.uniqueLevel ?? 1)) {
            const it = giveUnique(s, p, ch.unique, now);
            if (it && unique === undefined) unique = it.id;
          } else promiseUnique(s, p, ch.unique, ch.uniqueLevel ?? 1, now);
        }
        if (ch.acts.some((a) => a.kind === 'battle' && !!a.sunken) && !(p.hero.seals?.dragon ?? 0)) grantSeal(s, p, 'dragon', now);
      }
    // No experience, as the Turtle's replay: it is played again for the piece and the seal.
    event(s, now, 'side', { pid: who[0] ?? '', data: { saga: id, kind: 'replay', chapter: ticket.chapter, won } });
    return { counted: false, won, ...(unique !== undefined ? { unique } : {}) };
  }
  const st = sideMut(s, id);
  for (const f of battle.clues?.found ?? []) {
    if (st.clues.some((c) => c.id === f.id) || !findPlayer(s, f.pid)) continue;
    st.clues.push({ id: f.id, pid: f.pid, at: now });
    event(s, now, 'side', { pid: f.pid, data: { saga: id, kind: 'clue', clue: f.id } });
  }
  // What a won fight leaves on the board by itself.
  const act = sideDef(id)?.chapters[ticket.chapter]?.acts[ticket.act];
  if (won && act?.kind === 'battle') give(st, act.winClues, who[0] ?? '', now);
  // An outing after the Uro pays like a won fight: each one is a day's hunting.
  if (won || (battle.hunt?.wounds.length ?? 0) > 0 || (battle.copies?.fallen.length ?? 0) > 0) for (const pid of who) grantXp(s, pid, SIDE.actXp, now);
  if (st.chapter !== ticket.chapter || st.act !== ticket.act || sideBlocked(s, id)) return { counted: false, won };
  // The drive: every boar in the pen past the ones asked for pays the clan's extra.
  const herd = battle.rules?.herd;
  if (won && herd && battle.herd) for (let i = herd.need; i < battle.herd.penned; i++) addRes(s.storage, SIDE.herd.extra);
  st.tries++;
  // The copies felled stay felled, won or not.
  if (battle.rules?.copies && battle.copies?.fallen.length) st.flags = [...new Set([...(st.flags ?? []), ...battle.copies.fallen.map((pid) => `lid.copy.${pid}`)])];
  // The hunt: what the outing did stays, and the Uro falls once its wounds add up (whichever outing it was).
  if (battle.rules?.hunt && battle.hunt && act?.kind === 'battle') {
    const done = huntApply(s, st, act, battle, who, now, sideChapter(s, id)?.toughBp ?? 10000);
    if (!done) {
      for (const pid of who) if (!st.played.includes(pid)) st.played.push(pid);
      return { counted: true, won: false };
    }
    advance(s, id, who, now);
    return { counted: true, won: true };
  }
  if (!won) {
    for (const pid of who) if (!st.played.includes(pid)) st.played.push(pid);
    return { counted: true, won };
  }
  if (act?.kind === 'battle' && act.ragna) ragnaApply(s, st, battle, now);
  if (act?.kind === 'battle' && act.rising) risingApply(s, st, battle, now);
  if (act?.kind === 'battle' && act.fair) fairApply(st, battle);
  // The dragon's heart brought out: an ember of it for everyone who played the chapter's acts, and it stays at the forge.
  if (act?.kind === 'battle' && act.flight && battle.drink?.heart) {
    for (const pid of new Set([...st.played, ...who])) {
      const pl = findPlayer(s, pid);
      if (!pl) continue;
      pl.hero.dragonEmbers = (pl.hero.dragonEmbers ?? 0) + 1;
      event(s, now, 'side', { pid, data: { saga: 'shore', kind: 'ember' } });
    }
  }
  // The Drowned Dragon felled: the dragon's seal (the fourth awakening's) for everyone who played the chapter's acts.
  if (act?.kind === 'battle' && act.sunken) for (const pid of new Set([...st.played, ...who])) {
    const pl = findPlayer(s, pid);
    if (pl) grantSeal(s, pl, 'dragon', now);
  }
  // The night of the bell over: «de la campana», for everyone who played one of its acts.
  if (act?.kind === 'battle' && act.bastion === 'dawn') for (const pid of new Set([...st.played, ...who])) {
    const pl = findPlayer(s, pid);
    if (!pl || pl.hero.bell) continue;
    pl.hero.bell = true;
    event(s, now, 'side', { pid, data: { saga: 'lid', kind: 'bell' } });
  }
  advance(s, id, who, now);
  return { counted: true, won };
};

/**
 * Ragna's end, for the whole campaign: felled in the graveyard, she is felled for good (her relic to one of those who
 * fought her, see `unsleepingRelicTo`; her axe by the keep); knelt at the clan's call, she went home. Either way no wave brings her again.
 */
const ragnaApply = (s: CampaignState, st: SideState, battle: BattleState, now: Ms): void => {
  const out = battle.stats.unsleeping;
  if (out?.id === 'ragna' && out.outcome === 'slain') {
    st.flags = [...new Set([...(st.flags ?? []), 'horn.slain'])];
    afterUnsleeping(s, now, s.mareaIndex, 0, out, battle.heroes.map((h) => ({ pid: h.pid, live: h.control === 'live' })));
  } else if (battle.call) {
    const all = (s.unsleeping ??= {});
    const r = (all.ragna ??= { returns: 0, marea: s.mareaIndex, wave: 0 });
    r.home = { at: now };
    delete r.hunt;
  }
};

/** Bruni's end, for the whole campaign: felled at the forge, he is felled for good (his relic to one of those who fought him, see `unsleepingRelicTo`); no wave brings him again. */
const risingApply = (s: CampaignState, st: SideState, battle: BattleState, now: Ms): void => {
  const out = battle.stats.unsleeping;
  if (out?.id !== 'bruni' || out.outcome !== 'slain') return;
  st.flags = [...new Set([...(st.flags ?? []), 'rails.slain'])];
  afterUnsleeping(s, now, s.mareaIndex, 0, out, battle.heroes.map((h) => ({ pid: h.pid, live: h.control === 'live' })));
};

/**
 * An outing's toll on the Uro: its wounds (by hero) off the life it has now, the traps that went off taken away and the
 * ones laid added; each hero's wound goes into the chronicle. True: it fell (in this outing, or its wounds added up).
 */
const huntApply = (s: CampaignState, st: SideState, act: SideBattle, battle: BattleState, who: string[], now: Ms, tough: number): boolean => {
  const r = battle.rules?.hunt;
  const b = battle.hunt;
  if (!r || !b) return false;
  const k = SIDE.hunt;
  const h = { ...huntAt(st.hunt ?? huntFresh(s, act, tough, now), now) };
  h.wounds = h.wounds.map((w) => [w[0], w[1]]);
  let dealt = 0;
  for (const w of b.wounds) {
    if (!findPlayer(s, w.pid) || w.dmg <= 0) continue;
    dealt += w.dmg;
    const mine = h.wounds.find((x) => x[0] === w.pid);
    if (mine) mine[1] += w.dmg;
    else h.wounds.push([w.pid, w.dmg]);
  }
  h.hp = imax(0, h.hp - dealt);
  h.last = now;
  h.traps = h.traps.filter((x) => !b.sprung.includes(x.id));
  for (const tr of b.traps) {
    if (tr.old || h.traps.length >= k.trap.max || !findPlayer(s, tr.pid)) continue;
    h.trapId++;
    h.traps.push({ id: h.trapId, x: tr.x, y: tr.y, pid: tr.pid });
  }
  const fell = battle.result === 'won' || h.hp <= 0;
  for (const w of b.wounds) if (w.dmg > 0 && findPlayer(s, w.pid)) event(s, now, 'side', { pid: w.pid, data: { saga: 'horn', kind: 'wound', dmg: w.dmg, left: fell ? 0 : idiv(h.hp * 100, imax(1, h.max)) } });
  if (fell) {
    const by = b.by && findPlayer(s, b.by) ? b.by : (b.wounds[b.wounds.length - 1]?.pid ?? who[0] ?? '');
    event(s, now, 'side', { pid: by, data: { saga: 'horn', kind: 'uro' } });
    st.hunt = { ...h, hp: 0 };
    return true;
  }
  st.hunt = h;
  return false;
};
